<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { docsApi } from '$lib/api/docs';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { Doc } from '$lib/types';

  const { projectId, doc, onClose, onSaved }: { projectId: number; doc: Doc | null; onClose: () => void; onSaved?: (doc: Doc) => void } =
    $props();
  const queryClient = useQueryClient();
  const editing = !!doc;

  let title = $state(doc?.title ?? '');
  let body = $state(doc?.body ?? '');
  let formError = $state('');

  const mutation = createMutation(() => ({
    mutationFn: () => (editing ? docsApi.update(doc!.id, { title: title.trim(), body }) : docsApi.create(projectId, { title: title.trim(), body })),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['project-docs', projectId] });
      queryClient.invalidateQueries({ queryKey: ['doc', saved.id] });
      onClose();
      toast.success('Document saved');
      onSaved?.(saved);
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!title.trim()) {
      formError = 'Title is required';
      return;
    }
    formError = '';
    mutation.mutate();
  }
</script>

<Modal title={editing ? 'Edit document' : 'New document'} {onClose} wide>
  {#snippet footer()}
    <span class="spacer"></span>
    <button type="button" class="btn btn-secondary" onclick={onClose}>Cancel</button>
    <button type="submit" form="doc-form" class="btn btn-primary" disabled={!title.trim() || mutation.isPending}>
      {editing ? 'Save' : 'Create'}
    </button>
  {/snippet}

  <form id="doc-form" onsubmit={submit}>
    <div class="field">
      <label class="label" for="doc-title">Title</label>
      <input id="doc-title" class="input" placeholder="e.g. Backend architecture overview" bind:value={title} />
    </div>
    <div class="field">
      <label class="label" for="doc-body">Content</label>
      <textarea
        id="doc-body"
        class="textarea"
        style="min-height:220px;font-family:inherit"
        placeholder={'Write in plain text. Start a line with "# " for a heading.'}
        bind:value={body}
      ></textarea>
    </div>
    {#if formError}<p style="color:var(--danger);font-size:13px">{formError}</p>{/if}
  </form>
</Modal>
